import { useState } from 'react';
import { reportsApi } from '../services/api';
import { EfficiencyReport } from '../types/models';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  BarChart3,
  Download,
  FileSpreadsheet,
  Clock,
  CheckCircle,
  Wrench,
  TrendingUp,
} from 'lucide-react';

export default function ReportsPage() {
  const [report, setReport] = useState<EfficiencyReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(
    format(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState(
    format(endOfMonth(new Date()), 'yyyy-MM-dd')
  );

  const handleGenerateReport = async () => {
    setIsLoading(true);
    try {
      const data = await reportsApi.getEfficiencyReport(startDate, endDate) as EfficiencyReport;
      setReport(data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = (format: 'csv' | 'print') => {
    if (!report) return;

    if (format === 'csv') {
      const headers = ['Placa', 'Vehículo', 'Cliente', 'Estado', 'Horas'];
      const rows = report.jobs.map(j => [
        j.vehicle?.placa || '',
        `${j.vehicle?.marca || ''} ${j.vehicle?.modelo || ''}`,
        j.cliente_nombre,
        j.estado_id,
        j.vehicle ? String(report.jobs.find(jb => jb.id === j.id)?.vehicle?.placa ? 0 : 0) : '0',
      ]);

      const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-taller-${startDate}-${endDate}.csv`;
      a.click();
    }

    if (format === 'print') {
      window.print();
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          Reportes y Estadísticas
        </h1>
        <p className="text-gray-500">Análisis de eficiencia del taller</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <TrendingUp className="h-5 w-5" />
            )}
            Generar Reporte
          </button>
        </div>
      </div>

      {report && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Jobs</p>
                  <p className="text-3xl font-bold text-gray-800">{report.total_jobs}</p>
                </div>
                <Wrench className="h-10 w-10 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completados</p>
                  <p className="text-3xl font-bold text-green-600">{report.completed_jobs}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Horas Totales</p>
                  <p className="text-3xl font-bold text-gray-800">{report.total_hours}h</p>
                </div>
                <Clock className="h-10 w-10 text-orange-500" />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Eficiencia</p>
                  <p className="text-3xl font-bold text-purple-600">{report.efficiency_rate}%</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Chart Placeholder & Export */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Resumen del Período</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport('csv')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm"
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  Exportar CSV
                </button>
                <button
                  onClick={() => handleExport('print')}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
                >
                  <Download className="h-4 w-4" />
                  Imprimir
                </button>
              </div>
            </div>

            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p>Período: {format(new Date(startDate), 'dd MMM yyyy')} - {format(new Date(endDate), 'dd MMM yyyy')}</p>
              <p className="text-sm mt-2">
                {report.total_jobs} jobs procesados | {report.completed_jobs} completados | {report.total_hours}h trabajadas
              </p>
            </div>
          </div>

          {/* Jobs List */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">Detalle de Jobs</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Placa</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Vehículo</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Cliente</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Estado</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Ingreso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {job.vehicle?.placa || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {job.vehicle?.marca} {job.vehicle?.modelo}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{job.cliente_nombre}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          job.estado_id === 60 || job.estado_id === 70
                            ? 'bg-green-100 text-green-700'
                            : job.estado_id === 80
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {job.estado_id === 10 ? 'Recibido' :
                           job.estado_id === 20 ? 'Diagnóstico' :
                           job.estado_id === 40 ? 'En Proceso' :
                           job.estado_id === 50 ? 'Espera' :
                           job.estado_id === 60 ? 'Terminado' :
                           job.estado_id === 70 ? 'Entregado' :
                           job.estado_id === 80 ? 'Cancelado' : 'Otro'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {format(new Date(job.fecha_ingreso), 'dd MMM yyyy')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {report.jobs.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No hay jobs en este período
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
